import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the send-it-safely start screen. Three short
 * slides (what/who and why a human is the control, what to watch for, key terms)
 * shown in the reader while the 3D room downloads. Content only; the reader UI
 * lives in ui/StartReader.tsx and the art toolkit in ui/readingArt.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. share dialog: two people with the same name, one permission pick */
/* ------------------------------------------------------------------ */
function PersonRow({
  y,
  addr,
  badge,
  tone,
  picked,
}: {
  y: number;
  addr: string;
  badge: string;
  tone: string;
  picked: boolean;
}) {
  return (
    <g>
      <rect
        x="16.5"
        y={y + 0.5}
        width="327"
        height="42"
        rx="9"
        fill={C.panel2}
        stroke={picked ? C.mint : C.line}
        strokeOpacity={picked ? 0.7 : 1}
      />
      <circle cx="38" cy={y + 22} r="12" fill={tone} fillOpacity="0.14" />
      <text x="38" y={y + 26} fontSize="10.5" fontWeight="700" fill={tone} textAnchor="middle">
        ML
      </text>
      <text x="58" y={y + 19} fontSize="12.5" fontWeight="700" fill={C.text}>
        Morgan Lee
      </text>
      <text x="58" y={y + 34} fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        {addr}
      </text>
      <rect x="262" y={y + 12} width="68" height="20" rx="10" fill={tone} fillOpacity="0.12" stroke={tone} strokeOpacity="0.45" />
      <text x="296" y={y + 26} fontSize="10.5" fontWeight="700" fill={tone} textAnchor="middle">
        {badge}
      </text>
      {picked ? (
        <g>
          <circle className="sr-anim-ring" cx="338" cy={y + 6} r="5.5" fill="none" stroke={C.mint} strokeWidth="1.5" />
          <circle cx="338" cy={y + 6} r="5.5" fill={C.mint} />
          <polyline points={`335.4,${y + 6} 337.3,${y + 8} 340.6,${y + 4}`} fill="none" stroke={C.code} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      ) : null}
    </g>
  );
}

function ShareArt() {
  const H = 298;
  return (
    <Frame label="Example: sharing a file" aria="A share dialog listing two people named Morgan Lee, one in your organisation and one external, with a view, comment or edit choice" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Share
      </text>
      <text x="344" y="53" fontSize="11" fontFamily={MONO} fill={C.muted} textAnchor="end">
        workshop-schedule
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <text x="16" y="88" fontSize="10.5" fill={C.muted}>
        Share with
      </text>
      <PersonRow y={96} addr="morgan.lee@team.example" badge="Our org" tone={C.mint} picked />
      <PersonRow y={148} addr="morgan.lee@outside.example" badge="External" tone={C.amber} picked={false} />

      <text x="16" y="213" fontSize="10.5" fill={C.muted}>
        They can
      </text>
      <rect x="16.5" y="220.5" width="104" height="28" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="68.5" y="238.5" fontSize="12" fill={C.muted} textAnchor="middle">
        View
      </text>
      <rect x="128.5" y="220.5" width="104" height="28" rx="8" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.6" />
      <text x="180.5" y="238.5" fontSize="12" fontWeight="700" fill={C.mint} textAnchor="middle">
        Comment
      </text>
      <rect x="240.5" y="220.5" width="103" height="28" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="292" y="238.5" fontSize="12" fill={C.muted} textAnchor="middle">
        Edit
      </text>

      <rect x="16.5" y="258.5" width="327" height="30" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <circle className="sr-anim-ring" cx="33" cy="273.5" r="5.5" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <circle cx="33" cy="273.5" r="1.8" fill={C.amber} />
      <text x="46" y="277.5" fontSize="12" fontWeight="600" fill={C.amber}>
        Same name. Check the address.
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. consent card: an unknown app asks for far more than the task     */
/* ------------------------------------------------------------------ */
function ScopeRow({ y, label, level }: { y: number; label: string; level: string }) {
  return (
    <g>
      <circle cx="28" cy={y} r="4" fill={C.red} fillOpacity="0.18" stroke={C.red} strokeOpacity="0.7" />
      <line x1="25.5" y1={y} x2="30.5" y2={y} stroke={C.red} strokeWidth="1.4" strokeLinecap="round" />
      <text x="42" y={y + 4.2} fontSize="12.5" fill={C.text}>
        {label}
      </text>
      <text x="334" y={y + 4.2} fontSize="10.5" fontWeight="700" fill={C.red} textAnchor="end" letterSpacing="0.5">
        {level}
      </text>
    </g>
  );
}

function ConsentArt() {
  const H = 300;
  return (
    <Frame label="Example: an app asks to connect" aria="A permission request from an unapproved viewer app asking for all files, mailbox access and sending mail, with a cancel and report button" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Viewer wants access
      </text>
      <rect className="sr-anim-pulse" x="258" y="38" width="86" height="22" rx="11" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.5" />
      <text x="301" y="53" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle" letterSpacing="0.4">
        NOT APPROVED
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <text x="16" y="92" fontSize="10.5" fill={C.muted}>
        This app wants to
      </text>
      <ScopeRow y={110} label="See all your files" level="ALL" />
      <line x1="16" y1="123.5" x2="344" y2="123.5" stroke={C.line} strokeOpacity="0.6" />
      <ScopeRow y={140} label="Read your mailbox" level="ALL" />
      <line x1="16" y1="153.5" x2="344" y2="153.5" stroke={C.line} strokeOpacity="0.6" />
      <ScopeRow y={170} label="Send mail as you" level="ALL" />

      <rect x="16.5" y="188.5" width="327" height="34" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="203" fontSize="10.5" fill={C.muted}>
        The task only needs
      </text>
      <text x="29" y="216" fontSize="11.5" fontFamily={MONO} fill={C.mint}>
        one document, view only
      </text>

      <rect x="16.5" y="236.5" width="188" height="48" rx="10" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.6" />
      <circle className="sr-anim-ring" cx="36" cy="260.5" r="5.5" fill="none" stroke={C.mint} strokeWidth="1.5" />
      <circle cx="36" cy="260.5" r="2" fill={C.mint} />
      <text x="116" y="264.5" fontSize="12.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        Cancel and report
      </text>
      <rect x="214.5" y="236.5" width="129" height="48" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="279" y="264.5" fontSize="12.5" fill={C.muted} textAnchor="middle">
        Allow
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. access list: keep what the task needs, remove the rest           */
/* ------------------------------------------------------------------ */
type Who = "team" | "public" | "guest";

function WhoGlyph({ who }: { who: Who }) {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  if (who === "team") {
    return (
      <g {...s}>
        <circle cx="-3.5" cy="-3" r="3" />
        <path d="M-9 6 a5.5 5.5 0 0 1 11 0" />
        <circle cx="5" cy="-2" r="2.4" />
        <path d="M3 6 a4.5 4.5 0 0 1 7 0" />
      </g>
    );
  }
  if (who === "public") {
    return (
      <g {...s}>
        <circle cx="0" cy="0" r="8" />
        <ellipse cx="0" cy="0" rx="3.4" ry="8" />
        <line x1="-8" y1="0" x2="8" y2="0" />
      </g>
    );
  }
  return (
    <g {...s}>
      <circle cx="0" cy="-3.5" r="3.6" />
      <path d="M-7 8 a7 5.5 0 0 1 14 0" />
    </g>
  );
}

function AccessRow({ y, who, title, sub, keep }: { y: number; who: Who; title: string; sub: string; keep: boolean }) {
  const tone = keep ? C.mint : C.amber;
  return (
    <g>
      <rect x="16.5" y={y + 0.5} width="327" height="44" rx="9" fill={C.panel2} stroke={C.line} />
      <circle cx="40" cy={y + 22.5} r="16" fill={C.mint} fillOpacity="0.12" />
      <g transform={`translate(40 ${y + 22.5})`}>
        <WhoGlyph who={who} />
      </g>
      <text x="66" y={y + 20} fontSize="12.5" fontWeight="700" fill={C.text}>
        {title}
      </text>
      <text x="66" y={y + 35} fontSize="10.5" fill={C.muted}>
        {sub}
      </text>
      {keep ? (
        <g>
          <rect x="278" y={y + 12} width="54" height="21" rx="10.5" fill={tone} fillOpacity="0.12" stroke={tone} strokeOpacity="0.45" />
          <text x="305" y={y + 26.5} fontSize="10.5" fontWeight="700" fill={tone} textAnchor="middle">
            Keep
          </text>
        </g>
      ) : (
        <g>
          <rect className="sr-anim-pulse" x="270" y={y + 12} width="62" height="21" rx="10.5" fill={tone} fillOpacity="0.14" stroke={tone} strokeOpacity="0.6" />
          <text x="301" y={y + 26.5} fontSize="10.5" fontWeight="700" fill={tone} textAnchor="middle">
            Remove
          </text>
        </g>
      )}
    </g>
  );
}

function CloseArt() {
  const H = 296;
  return (
    <Frame label="Example: workshop is over" aria="An access list where the team and the public flyer link stay and the outside facilitator's access is removed" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Who has access
      </text>
      <text x="344" y="53" fontSize="11" fill={C.muted} textAnchor="end">
        3 paths
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      <AccessRow y={82} who="team" title="Project team" sub="Can edit the schedule" keep />
      <AccessRow y={134} who="public" title="Public flyer link" sub="Anyone with the link can view" keep />
      <AccessRow y={186} who="guest" title="Outside facilitator" sub="Can comment. Work finished." keep={false} />

      <rect x="16.5" y="244.5" width="327" height="38" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="260" fontSize="10.5" fill={C.muted}>
        Rule
      </text>
      <text x="29" y="274" fontSize="11.5" fontFamily={MONO} fill={C.soft}>
        close what the task no longer needs
      </text>
      <rect className="sr-anim-blink" x="273" y="264" width="1.5" height="12" rx="0.75" fill={C.mint} />
    </Frame>
  );
}

export const SEND_IT_SAFELY_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Share the file, not the risk.",
    illustration: ShareArt,
    caption: "The tool shares what you click. You choose what that is.",
    sections: [
      {
        h: "What this is about",
        body: "You\u2019re helping a small team run a workshop. Requests arrive in Mail: share a file with colleagues, an outside facilitator or the public. You pick the file, the people and the access.",
      },
      {
        h: "Who it\u2019s for",
        body: "Anyone who shares files or links at work. No special tools needed.",
      },
      {
        h: "Why your judgement matters",
        body: "A sharing tool can\u2019t tell that a name matches two people, or that one copy holds internal notes. It does what you click. You are the control.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "Where a quick share goes wrong.",
    illustration: ConsentArt,
    caption: "A polished consent page is not employer approval.",
    sections: [
      {
        h: "The wrong person or the wrong copy",
        body: "Two people can share a name, and a working copy can hold internal notes. Check the full address and send the version marked for that audience.",
      },
      {
        h: "More access than the task needs",
        body: "Edit when view is enough, or a public link when a few people need it. Give the least that gets the job done.",
      },
      {
        h: "Access you can\u2019t see",
        body: "A file can still be open through a broader folder. A new narrow share doesn\u2019t cancel it. Check the whole access list.",
      },
      {
        h: "Apps asking to connect",
        body: "An unexpected app asking for all files or your mailbox is a red flag. Cancel it and report it. Changing your password won\u2019t remove it.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: CloseArt,
    caption: "Keep what the task needs. Close the rest.",
    sections: [
      { h: "Permission", body: "What a person can do with a file: view, comment or edit." },
      { h: "Link type", body: "Who a link works for: named people, your organisation, or anyone who has it." },
      { h: "Least access", body: "Give only the access the task needs, and nothing more." },
      { h: "Inherited access", body: "Access a file gets from the folder above it, even if you never shared the file itself." },
      { h: "App permission", body: "What a connected app may read or do on your behalf, once you approve it." },
      { h: "Revoke", body: "Take access back when the work ends. A copy already downloaded stays on their side." },
    ],
  },
];
